import React from 'react';
import { useTranslation } from 'react-i18next';
import { TradeType, Direction } from '../../types/tradeBooking';
import { getEnabledCurrencies, getValidCurrency2Options } from '../../types/currency';
import { TradingEntity } from '../../services/api/clientService';
import { Bank } from '../../services/api/bankService';

interface Shortcut {
  id: string;
  name: string;
  tradeType?: TradeType;
  direction?: Direction;
  currency1?: string;
  amount?: number | null;
  currency2?: string;
  ourParty?: string;
  counterparty?: string;
}

interface ShortcutsModalProps {
  isOpen: boolean;
  shortcuts: Shortcut[];
  editingShortcut: Shortcut | null;
  isNewShortcut: boolean;
  tradingEntities: TradingEntity[];
  banks: Bank[];
  onClose: () => void;
  onNewShortcut: () => void;
  onEditShortcut: (shortcut: Shortcut) => void;
  onDuplicateShortcut: (shortcut: Shortcut) => void;
  onDeleteShortcut: (id: string) => void;
  onSaveShortcut: () => void;
  onCancelEdit: () => void;
  onUpdateEditingShortcut: (field: string, value: any) => void;
}

const ShortcutsModal: React.FC<ShortcutsModalProps> = ({
  isOpen,
  shortcuts,
  editingShortcut,
  isNewShortcut,
  tradingEntities,
  banks,
  onClose,
  onNewShortcut,
  onEditShortcut,
  onDuplicateShortcut,
  onDeleteShortcut,
  onSaveShortcut,
  onCancelEdit,
  onUpdateEditingShortcut,
}) => {
  const { t } = useTranslation();
  const currencies = getEnabledCurrencies();

  if (!isOpen) return null;

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-dialog shortcuts-modal" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2>{t('tradeBooking.shortcuts.manageTitle')}</h2>
        </div>
        <div className="modal-body shortcuts-modal-body">
          {/* Left Side: Shortcuts List */}
          <div className="shortcuts-list">
            <div className="shortcuts-list-header">
              <h3>{t('tradeBooking.shortcuts.yourShortcuts')}</h3>
              <button
                type="button"
                className="btn btn-primary btn-sm"
                onClick={onNewShortcut}
                disabled={shortcuts.length >= 10}
                title={shortcuts.length >= 10 ? t('tradeBooking.shortcuts.maxReached') : ''}
              >
                + {t('tradeBooking.shortcuts.addNew')}
              </button>
            </div>
            <div className="shortcuts-table">
              {shortcuts.length === 0 ? (
                <div className="empty-state">
                  <p>{t('tradeBooking.shortcuts.noShortcuts')}</p>
                </div>
              ) : (
                <table>
                  <thead>
                    <tr>
                      <th>{t('tradeBooking.shortcuts.name')}</th>
                      <th>{t('tradeBooking.shortcuts.type')}</th>
                      <th>{t('tradeBooking.shortcuts.actions')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {shortcuts.map((shortcut) => (
                      <tr key={shortcut.id}>
                        <td>{shortcut.name}</td>
                        <td>
                          {shortcut.tradeType ? t(`tradeBooking.tradeType.${shortcut.tradeType.toLowerCase()}`) : '-'}
                        </td>
                        <td className="actions-cell">
                          <button
                            type="button"
                            className="icon-btn"
                            onClick={() => onEditShortcut(shortcut)}
                            title={t('tradeBooking.shortcuts.edit')}
                          >
                            ✏️
                          </button>
                          <button
                            type="button"
                            className="icon-btn"
                            onClick={() => onDuplicateShortcut(shortcut)}
                            title={t('tradeBooking.shortcuts.duplicate')}
                          >
                            📋
                          </button>
                          <button
                            type="button"
                            className="icon-btn"
                            onClick={() => onDeleteShortcut(shortcut.id)}
                            title={t('tradeBooking.shortcuts.delete')}
                          >
                            🗑️
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          </div>

          {/* Right Side: Edit Form */}
          <div className="shortcut-edit-form">
            {editingShortcut ? (
              <>
                {/* Shortcut Name Header */}
                <div className="shortcut-name-header">
                  <input
                    type="text"
                    className="shortcut-name-input"
                    value={editingShortcut.name}
                    onChange={(e) => {
                      const value = e.target.value.slice(0, 50);
                      onUpdateEditingShortcut('name', value);
                    }}
                    placeholder={t('tradeBooking.shortcuts.namePlaceholder')}
                    autoFocus={isNewShortcut}
                    maxLength={50}
                  />
                </div>

                {/* Trade Type */}
                <div className="form-group">
                  <label className="form-label">{t('tradeBooking.tradeType.label')}</label>
                  <div className="button-group">
                    <button
                      type="button"
                      className={`btn-option ${editingShortcut.tradeType === TradeType.SPOT ? 'active' : ''}`}
                      onClick={() => onUpdateEditingShortcut('tradeType', TradeType.SPOT)}
                    >
                      {t('tradeBooking.tradeType.spot')}
                    </button>
                    <button
                      type="button"
                      className={`btn-option ${editingShortcut.tradeType === TradeType.FORWARD ? 'active' : ''}`}
                      onClick={() => onUpdateEditingShortcut('tradeType', TradeType.FORWARD)}
                    >
                      {t('tradeBooking.tradeType.forward')}
                    </button>
                  </div>
                </div>

                {/* Direction */}
                <div className="form-group">
                  <label className="form-label">{t('tradeBooking.tradeDetails.direction')}</label>
                  <div className="button-group">
                    <button
                      type="button"
                      className={`btn-option buy ${editingShortcut.direction === Direction.BUY ? 'active' : ''}`}
                      onClick={() => onUpdateEditingShortcut('direction', Direction.BUY)}
                    >
                      {t('tradeBooking.tradeDetails.buy')}
                    </button>
                    <button
                      type="button"
                      className={`btn-option sell ${editingShortcut.direction === Direction.SELL ? 'active' : ''}`}
                      onClick={() => onUpdateEditingShortcut('direction', Direction.SELL)}
                    >
                      {t('tradeBooking.tradeDetails.sell')}
                    </button>
                  </div>
                </div>

                {/* Currency 1 & Currency 2 */}
                <div className="form-row">
                  <div className="form-group">
                    <label className="form-label">{t('tradeBooking.tradeDetails.currency1')}</label>
                    <select
                      className="form-select"
                      value={editingShortcut.currency1 || ''}
                      onChange={(e) => {
                        const newCcy1 = e.target.value;
                        onUpdateEditingShortcut('currency1', newCcy1);
                        // Clear currency2 if it's no longer a valid pair
                        if (editingShortcut.currency2) {
                          const validOptions = getValidCurrency2Options(newCcy1);
                          if (!validOptions.some(c => c.code === editingShortcut.currency2)) {
                            onUpdateEditingShortcut('currency2', '');
                          }
                        }
                      }}
                    >
                      <option value="">{t('tradeBooking.tradeDetails.selectCurrency')}</option>
                      {currencies.map((curr) => (
                        <option key={curr.code} value={curr.code}>
                          {curr.code}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div className="form-group">
                    <label className="form-label">{t('tradeBooking.tradeDetails.currency2')}</label>
                    <select
                      className="form-select"
                      value={editingShortcut.currency2 || ''}
                      onChange={(e) => onUpdateEditingShortcut('currency2', e.target.value)}
                    >
                      <option value="">{t('tradeBooking.tradeDetails.selectCurrency')}</option>
                      {getValidCurrency2Options(editingShortcut.currency1).map((curr) => (
                        <option key={curr.code} value={curr.code}>
                          {curr.code}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>

                {/* Amount & Price */}
                <div className="form-row">
                  <div className="form-group">
                    <label className="form-label">{t('tradeBooking.tradeDetails.amount')}</label>
                    <input
                      type="text"
                      className="form-input"
                      value={editingShortcut.amount || ''}
                      onChange={(e) => onUpdateEditingShortcut('amount', e.target.value ? parseFloat(e.target.value) : null)}
                      placeholder=""
                    />
                  </div>

                </div>

                {/* Our Party & Counterparty */}
                <div className="form-row">
                  <div className="form-group">
                    <label className="form-label">{t('tradeBooking.tradeDetails.ourParty')}</label>
                    <select
                      className="form-select"
                      value={editingShortcut.ourParty || ''}
                      onChange={(e) => onUpdateEditingShortcut('ourParty', e.target.value)}
                    >
                      <option value="">{t('tradeBooking.tradeDetails.selectParty')}</option>
                      {tradingEntities.map((entity) => (
                        <option key={entity.id} value={entity.name}>
                          {entity.isParent ? entity.name : `  └─ ${entity.name}`}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div className="form-group">
                    <label className="form-label">{t('tradeBooking.tradeDetails.counterparty')}</label>
                    <select
                      className="form-select"
                      value={editingShortcut.counterparty || ''}
                      onChange={(e) => onUpdateEditingShortcut('counterparty', e.target.value)}
                    >
                      <option value="">{t('tradeBooking.tradeDetails.selectParty')}</option>
                      {banks.map((bank) => (
                        <option key={bank.id} value={bank.name}>{bank.name}</option>
                      ))}
                    </select>
                  </div>
                </div>

                {/* Form Actions */}
                <div className="shortcut-form-actions">
                  <button type="button" className="btn btn-secondary" onClick={onCancelEdit}>
                    {t('tradeBooking.settings.cancel')}
                  </button>
                  <button type="button" className="btn btn-primary" onClick={onSaveShortcut}>
                    {t('tradeBooking.settings.save')}
                  </button>
                </div>
              </>
            ) : (
              <div className="empty-state">
                <p>{t('tradeBooking.shortcuts.selectOrCreate')}</p>
              </div>
            )}
          </div>
        </div>
        <div className="modal-footer">
          <button type="button" className="btn btn-secondary" onClick={onClose}>
            {t('tradeBooking.settings.cancel')}
          </button>
        </div>
      </div>
    </div>
  );
};

export default ShortcutsModal;
